import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import moment from 'moment';
import FormTextField from '../legacyForm/FormTextField';
import FormSelect from '../legacyForm/FormSelect';
import Grid from '@material-ui/core/Grid';
import Pesel from '../../utils/pesel';
import PhoneNumber from './voting/PhoneNumber';
import TerytAddress from './voting/TerytAddress';
import TechnikaItAddress from './voting/TechnikaItAddress';
import FormBirthDateField from '../legacyForm/FormBirthDateField';
import PeselFragment from '../legacyForm/PeselFragmentField';
import { Typography } from '@material-ui/core';
import FormAutocompleteField from '../legacyForm/FormAutocompleteField';
import FormBudgetVoteFilesUploadField from '../form/FormBudgetVoteFilesUploadField';

const fileSizeLimitMegabytes = 10;
const fileUploadAcceptedMimeTypesList = [
  'application/pdf',
  'image/*',
].join(', ');

function decodeBirthDate(peselString) {
  const digits = peselString.split('').map(c => parseInt(c, 10));
  let year = 1900 + digits[0] * 10 + digits[1];

  if (digits[2] >= 2 && digits[2] < 8) {
    year += Math.floor(digits[2] / 2) * 100;
  }

  if (digits[2] >= 8) {
    year -= 100;
  }

  const month = (digits[2] % 2) * 10 + digits[3];
  const day = digits[4] * 10 + digits[5];
  return new Date(year, month - 1, day);
}

const required = value => value ? undefined : 'Pole jest wymagane';
const pesel = value => new Pesel(value).isValid() ? undefined : 'Numer pesel nie jest prawidłowy';

const isOlderThan = (value, limit) => {
  const years = moment().diff(value, 'years',false);
  return years >= limit ? true : false;
};

const VoterData = ({ streetsAutocomplete, streetsAutocompleteData, terytEnabled,
  terytFilter, voterRequiredData, change,
  actions, phoneVerificationEnabled, values, verificationMethodId,
  manualVote, customMessages, budgetId, requiresParentalConsent, parentalConsentAgeLimit, disabled }) => {
  return (
    <div>
      {voterRequiredData.firstName && <div><Field
        name="voter.firstName"
        component={FormTextField}
        label="Imię"
        validate={voterRequiredData.firstNameOptional ? undefined : required}
        helperText={voterRequiredData.firstNameDescription}
        disabled={disabled}
      /></div>}
      {voterRequiredData.secondName && <div><Field
        name="voter.secondName"
        component={FormTextField}
        label="Drugie imię"
        validate={voterRequiredData.secondNameOptional ? undefined : required}
        helperText={voterRequiredData.secondNameDescription}
        disabled={disabled}
      /></div>}
      {voterRequiredData.lastName && <div><Field
        name="voter.lastName"
        component={FormTextField}
        label="Nazwisko"
        fullWidth
        validate={voterRequiredData.lastNameOptional ? undefined : required}
        helperText={voterRequiredData.lastNameDescription}
        disabled={disabled}
      /></div>}
      {voterRequiredData.address ? <React.Fragment>
        {terytEnabled
          ? <TerytAddress
            voterRequiredData={voterRequiredData}
            change={change}
            filter={terytFilter}
            addressRestrictedToCities={voterRequiredData.addressRestrictedToCities || []}
            disabled={disabled}
          />
          : streetsAutocomplete && streetsAutocompleteData
            ? <TechnikaItAddress
              voterRequiredData={voterRequiredData}
              change={change}
              streetsAutocompleteData={streetsAutocompleteData}
              cityId={values ? values.cityId : null}
              addressRestrictedToCities={voterRequiredData.addressRestrictedToCities || []}
              disabled={disabled}
            />
            : <div>
              <div>
                <Field
                  name="voter.address"
                  component={FormTextField}
                  label="Adres zamieszkania"
                  fullWidth
                  validate={voterRequiredData.addressOptional ? undefined : required}
                  disabled={disabled}
                />
                <Field
                  name="voter.number"
                  component={FormTextField}
                  label="Numer"
                  validate={voterRequiredData.addressOptional ? undefined : required}
                  disabled={disabled}
                />
                <Field
                  name="voter.localNumber"
                  component={FormTextField}
                  label="Numer lokalu"
                  disabled={disabled}
                />
              </div>
              <div>
                <Field
                  name="voter.zipCode"
                  component={FormTextField}
                  label="Kod pocztowy"
                  validate={voterRequiredData.addressOptional ? undefined : required}
                  disabled={disabled}
                />
                {voterRequiredData.addressRestrictedToCities && voterRequiredData.addressRestrictedToCities.length
                  ? <Field
                    name="voter.city"
                    component={FormAutocompleteField}
                    label="Miejscowość"
                    placeholder="Miejscowość"
                    validate={voterRequiredData.addressOptional ? undefined : required}
                    autocompleteData={voterRequiredData.addressRestrictedToCities.map(name => ({ id: name, name }))}
                    disabled={disabled}
                  />
                  : <Field
                    name="voter.city"
                    component={FormTextField}
                    label="Miejscowość"
                    validate={voterRequiredData.addressOptional ? undefined : required}
                    disabled={disabled}
                  />
                }
              </div>
            </div>}
        {voterRequiredData.addressDescription && <Typography variant="caption">{voterRequiredData.addressDescription}</Typography>}
      </React.Fragment> : null}
      <Grid container spacing={1}>
        {voterRequiredData.birthDate && <Grid item xs><Field
          name="voter.birthDate"
          component={FormBirthDateField}
          label="Data urodzenia"
          validate={voterRequiredData.birthDateOptional ? undefined : required}
          helperText={voterRequiredData.birthDateDescription}
          disabled={disabled}
        /></Grid>}
      </Grid>
      {voterRequiredData.birthPlace && <Field
        name="voter.birthPlace"
        fullWidth
        component={FormTextField}
        placeholder="Wprowadź pełną nazwę miejscowości"
        label="Miejsce urodzenia"
        validate={voterRequiredData.birthPlaceOptional ? undefined : required}
        helperText={voterRequiredData.birthPlaceDescription}
        disabled={disabled}
      />}
      <Grid container spacing={1}>
        {voterRequiredData.motherName && <Grid item xs><Field
          name="voter.motherName"
          component={FormTextField}
          label="Imię matki"
          validate={voterRequiredData.motherNameOptional ? undefined : required}
          helperText={voterRequiredData.motherNameDescription}
          disabled={disabled}
        /></Grid>}
        {voterRequiredData.fatherName && <Grid item xs><Field
          name="voter.fatherName"
          component={FormTextField}
          label="Imię ojca"
          validate={voterRequiredData.fatherNameOptional ? undefined : required}
          helperText={voterRequiredData.fatherNameDescription}
          disabled={disabled}
        /></Grid>}
        {voterRequiredData.motherLastName && <Grid item xs><Field
          name="voter.motherLastName"
          component={FormTextField}
          label="Nazwisko rodowe matki"
          fullWidth
          validate={voterRequiredData.motherLastNameOptional ? undefined : required}
          helperText={voterRequiredData.motherLastNameDescription}
          disabled={disabled}
        /></Grid>}
        {voterRequiredData.citizenship && <Grid item xs><Field
          name="voter.citizenship"
          component={FormTextField}
          label="Obywatelstwo"
          validate={voterRequiredData.citizenshipOptional ? undefined : required}
          helperText={voterRequiredData.citizenshipDescription}
          disabled={disabled}
        /></Grid>}
        {voterRequiredData.pesel && <Grid item xs><Field
          name="voter.pesel"
          component={FormTextField}
          label="Pesel"
          validate={voterRequiredData.peselOptional ? pesel : [required, pesel]}
          helperText={voterRequiredData.peselDescription}
          disabled={disabled}
        /></Grid>}
      </Grid>
      {voterRequiredData.peselFragment && <div>
        <Field
          name="voter.peselFragment"
          component={PeselFragment}
          label="Numer PESEL - wybrane cyfry"
          peselFragmentMask={voterRequiredData.peselFragmentMask}
          validate={voterRequiredData.peselFragmentOptional ? undefined : required}
          helperText={voterRequiredData.peselFragmentDescription}
          disabled={disabled}
        />
      </div>}
      {voterRequiredData.cityCard && <div>
        <Field
          name="voter.cityCard"
          component={FormTextField}
          label={customMessages.cityCard || "Numer karty"}
          validate={voterRequiredData.cityCardOptional ? undefined : required}
          helperText={voterRequiredData.cityCardDescription}
          disabled={disabled}
        />
      </div>}
      {
        voterRequiredData.phoneNumber && <div>
          <PhoneNumber
            voterRequiredData={voterRequiredData}
            change={change}
            phoneVerificationEnabled={phoneVerificationEnabled}
            manualVote={manualVote}
            onStartVerification={phoneVerificationEnabled ? (phone, verificationMethodId) => actions.startPhoneNumberVerification(budgetId, phone, verificationMethodId) : null}
            helperText={voterRequiredData.phoneNumberDescription}
            verificationMethodId={verificationMethodId}
            disabled={disabled}
          />
        </div>
      }
      <Grid container spacing={0}>
        {voterRequiredData.email && <Grid item xs><Field
          name="voter.email"
          component={FormTextField}
          label="Adres e-mail"
          type="email"
          validate={voterRequiredData.emailOptional ? undefined : required}
          helperText={voterRequiredData.emailDescription}
          disabled={disabled}
        /></Grid>}
        {voterRequiredData.gender && <Grid item xs><Field
          name="voter.gender"
          component={FormSelect}
          label="Płeć"
          values={[{ value: 'female', name: 'kobieta' }, { value: 'male', name: 'mężczyzna' }]}
          validate={voterRequiredData.genderOptional ? undefined : required}
          helperText={voterRequiredData.genderDescription}
          disabled={disabled}
        /></Grid>}
        {voterRequiredData.age && <Grid item xs><Field
          name="voter.age"
          component={FormSelect}
          label="Wiek"
          values={[{ value: 'lessThan30', name: 'poniżej 30 lat' }, { value: 'between3145', name: 'od 31 do 45 lat' }, { value: 'between4660', name: 'od 46 do 60 lat' }, { value: 'greaterThan60', name: 'powyżej 60 lat' }]}
          validate={voterRequiredData.ageOptional ? undefined : required}
          helperText={voterRequiredData.ageDescription}
          disabled={disabled}
        /></Grid>}
        {voterRequiredData.status && <Grid item xs><Field
          name="voter.status"
          component={FormSelect}
          label="Status zawodowy"
          values={[{ value: 'student', name: 'uczeń/student' }, { value: 'working', name: 'pracujący' }, { value: 'unemployed', name: 'niepracujący' }, { value: 'pensioner', name: 'emeryt/rencista' }]}
          validate={voterRequiredData.statusOptional ? undefined : required}
          helperText={voterRequiredData.statusDescription}
          disabled={disabled}
        /></Grid>}
      </Grid>
      {requiresParentalConsent && values &&
        (
          (voterRequiredData.birthDate && values.birthDate && !isOlderThan(values.birthDate, parentalConsentAgeLimit) ||
          (voterRequiredData.pesel && values.pesel && !isOlderThan(decodeBirthDate(values.pesel), parentalConsentAgeLimit))
        )) ? <div>
        <div>
          <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 16 }}>
            Zgoda opiekuna prawnego. Pole wymagane jeśli osoba głosująca jest osobą poniżej {parentalConsentAgeLimit} lat.
          </Typography>
          <Field
            validate={required}
            name="parentalConsentFile"
            component={FormBudgetVoteFilesUploadField}
            budgetId={budgetId}
            fileSizeLimitMegabytes={fileSizeLimitMegabytes}
            filesCountLimit={1}
            acceptedMimeTypes={fileUploadAcceptedMimeTypesList}
            disabled={disabled}
            //onDeleteUploadedFile={onDeleteUploadedFile}
          />
          <Typography variant="caption" color="textSecondary" display="block">
            {`Można załączyć maksymalnie 1 plik o rozmiarze nieprzekraczającym ${fileSizeLimitMegabytes} MB`}
          </Typography>
        </div>
      </div> : null}
    </div >
  );
};

VoterData.defaultProps = {
  restrictedToCities: ['polna']
};

VoterData.propTypes = {
  voterRequiredData: PropTypes.object.isRequired,
  streetsAutocomplete: PropTypes.bool.isRequired,
  streetsAutocompleteData: PropTypes.array,
  terytEnabled: PropTypes.bool,
  change: PropTypes.func.isRequired,
  terytFilter: PropTypes.string,
  phoneVerificationEnabled: PropTypes.bool.isRequired,
  manualVote: PropTypes.bool,
  actions: PropTypes.object,
  values: PropTypes.object,
  verificationMethodId: PropTypes.string,
  restrictedToCities: PropTypes.array,
  budgetId: PropTypes.string.isRequired,
  customMessages: PropTypes.object,
  requiresParentalConsent: PropTypes.bool,
  parentalConsentAgeLimit: PropTypes.number,
  disabled: PropTypes.bool,
};

export default VoterData;



// WEBPACK FOOTER //
// ./src/components/budget/VoterData.js